import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import {
  getUrlParamsFetch,
  notFoundIdHandler,
  urlBackend,
} from '@/shared';
import {
  AxiosCodeEnum,
  Establecimiento,
  EstablecimientosPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';

import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchEstablecimientos = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetEstablecimientosParams>) => {
  return useQuery({
    queryKey: ['establecimientos', ...Object.values(params ?? {})],
    queryFn: () => getEstablecimientos(params),
    enabled: enabled,
  });
};

export const useGetEstablecimiento = (id: string) => {
  return useQuery({
    queryKey: ['establecimiento', id],
    queryFn: () => getEstablecimiento(id),
    retry: false,
  });
};

export const useCreateEstablecimiento = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: createEstablecimiento,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['establecimientos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
      if (res.code === AxiosCodeEnum.CREATED) {
        setAlert(true, res.message, res.status);
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateEstablecimiento = ({
  navigate,
  returnUrl,
  enableRedirect = true,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: updateEstablecimiento,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['establecimientos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      if (res.code === AxiosCodeEnum.SUCCESS) {
        setAlert(true, res.message, res.status);
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteEstablecimiento = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEstablecimiento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establecimientos'] });
    },
  });
};

///* axios
export type GetEstablecimientosParams = Partial<Establecimiento> & {
  page?: number;
  page_size?: number;
};
export type CreateEstablecimientoParams = Omit<
  Establecimiento,
  'id_establecimiento'
>;
export type UpdateEstablecimientoParams = {
  id: string;
  data: CreateEstablecimientoParams;
};

export const getEstablecimientos = async (
  params?: GetEstablecimientosParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});

  return get<EstablecimientosPagingRes>(
    `${urlBackend}api/v1/establecimiento/?${queryParams}`,
    true
  );
};

export const getEstablecimiento = async (id: string) => {
  const res = await get<Establecimiento>(
    `${urlBackend}api/v1/establecimiento/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createEstablecimiento = async (
  data: CreateEstablecimientoParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await post<Establecimiento>(
    `${urlBackend}api/v1/establecimiento/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateEstablecimiento = async ({
  id,
  data,
}: UpdateEstablecimientoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await put<Establecimiento>(
    `${urlBackend}api/v1/establecimiento/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteEstablecimiento = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await remove<Establecimiento>(
    `${urlBackend}api/v1/establecimiento/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
